import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { shiftDay, shiftHours, weekStartOf } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { WeekNav } from "@/components/app/lists/week-nav";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacilityToday } from "@/hooks/use-facility-today";
import { orpc } from "@/lib/orpc";
import { readState } from "@/lib/query/read-state";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { hours, ShiftLine } from "./-components/shift-line";

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute("/_authenticated/you/shifts/")({
  validateSearch: (search: Record<string, unknown>): { week?: string } => ({
    week: typeof search.week === "string" && DAY.test(search.week) ? search.week : undefined,
  }),
  component: MyShifts,
});

const plural = (count: number, word: string) => `${count} ${count === 1 ? word : `${word}s`}`;

/** Your own week, any week. The next shift is the sentence at the top: it is the question this page
 * answers. */
function MyShifts() {
  const today = useFacilityToday();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const thisWeek = weekStartOf(today);
  const weekStart = search.week ? weekStartOf(search.week) : thisWeek;
  const days = Array.from({ length: 7 }, (_, index) => shiftDay(weekStart, index));

  const week = useQuery(
    orpc.workforce.shifts.mine.queryOptions({
      input: { from: weekStart, to: shiftDay(weekStart, 6) },
    }),
  );
  const ahead = useQuery(
    orpc.workforce.shifts.mine.queryOptions({
      input: { from: today, to: shiftDay(today, 60) },
    }),
  );

  const rows = (week.data ?? []).filter((shift) => shift.status !== "cancelled");
  const worked = rows.reduce((sum, shift) => sum + shiftHours(shift.startTime, shift.endTime), 0);
  const next = (ahead.data ?? []).find(
    (shift) =>
      shift.status === "scheduled" &&
      (shift.date > today || shift.startTime >= format(new Date(), "HH:mm")),
  );
  const read = readState(week, 1);

  const meta =
    ahead.data === undefined
      ? "Where and when you work, this week and any other."
      : next
        ? `Your next shift: ${next.shiftType}, ${next.date === today ? "today" : format(parseISO(next.date), "EEEE d MMM")}, ${hours(next.startTime, next.endTime)}${next.facilityName ? ` at ${next.facilityName}` : ""}.`
        : "No shifts coming up in the next 60 days.";

  return (
    <>
      <PageHeader title="My shifts" meta={meta} />
      <div className="my-4 flex flex-wrap items-center justify-between gap-3">
        <WeekNav
          onChange={(chosen) =>
            void navigate({
              to: ".",
              search: (previous) => ({
                ...previous,
                week: chosen === thisWeek ? undefined : chosen,
              }),
            })
          }
          thisWeek={thisWeek}
          weekStart={weekStart}
        />
        {read === "ready" ? (
          <span className="text-muted-foreground text-sm tabular-nums">
            {rows.length === 0
              ? "Nothing this week"
              : `${plural(rows.length, "shift")}, ${worked} hours`}
          </span>
        ) : null}
      </div>

      {read === "loading" ? (
        <Skeleton className="h-96 w-full rounded-xl" />
      ) : read === "failed" ? (
        <EmptyState
          title="Could not load your shifts"
          description="Nothing here means nothing was read, not that you are off."
          action={
            <Button disabled={week.isFetching} onClick={() => void week.refetch()}>
              Try again
            </Button>
          }
        />
      ) : (
        <ol className="bg-card divide-y overflow-hidden rounded-xl border">
          {days.map((day) => {
            const isToday = day === today;
            const onDay = (week.data ?? []).filter((shift) => shift.date === day);
            return (
              <li
                key={day}
                className={cn(
                  "flex min-h-16 gap-4 px-4 py-3 sm:items-center",
                  isToday && "bg-primary-tint",
                  day < today && "text-muted-foreground",
                )}
              >
                <div className="w-20 shrink-0 sm:w-28">
                  <p className={cn("text-base font-semibold", isToday && "text-primary-ink")}>
                    {format(parseISO(day), "EEEE")}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {format(parseISO(day), "d MMM")}
                    {isToday ? ", Today" : ""}
                  </p>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:flex-row sm:flex-wrap">
                  {onDay.length === 0 ? (
                    <span
                      className={cn(
                        "self-center text-sm",
                        /* Faint ink drops under 4.5:1 on today's tint. */
                        isToday ? "text-muted-foreground" : "text-faint",
                      )}
                    >
                      Off
                    </span>
                  ) : (
                    onDay.map((shift) => <ShiftLine key={shift.id} shift={shift} />)
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </>
  );
}
